// src/pages/PostServicePage.js
// Public service submission — any signed-in user, not just organisers.
// Self-submitted listings are auto-claimed by the poster (see createService
// in db.js) — you own what you post yourself. Goes live immediately;
// moderateService (Cloud Function) pulls flagged content back to
// pending_review for admin approval — same pattern as Events and Skill Share.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { createService } from '../lib/db';
import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'health',         label: 'Health & Medical',   emoji: '🏥' },
  { value: 'legal_financial',label: 'Legal & Financial',  emoji: '💼' },
  { value: 'food_drink',     label: 'Food & Drink',       emoji: '🍽️' },
  { value: 'accommodation',  label: 'Accommodation',      emoji: '🛏️' },
  { value: 'beauty',         label: 'Beauty & Hair',      emoji: '💇' },
  { value: 'sport_leisure',  label: 'Sport & Leisure',    emoji: '⚽' },
  { value: 'civic',          label: 'Civic & Community',  emoji: '🏛️' },
  { value: 'worship',        label: 'Worship',            emoji: '⛪' },
  { value: 'education',      label: 'Education',          emoji: '🎓' },
  { value: 'transport',      label: 'Transport',          emoji: '🚌' },
  { value: 'home_trade',     label: 'Home & Trade',       emoji: '🔧' },
  { value: 'shopping',       label: 'Shops & Retail',     emoji: '🛍️' },
  { value: 'services',       label: 'Other Services',     emoji: '🏘️' },
];

const PostServicePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '', category: 'services', description: '',
    phone: '', email: '', website: '', hours: '', address: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.phone.trim() && !form.email.trim() && !form.website.trim()) {
      e.contact = 'Add at least one way for people to get in touch — phone, email, or website';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { toast.error('Please fill in the required fields'); return; }
    setSaving(true);
    try {
      let logoUrl = null;
      if (logoFile) {
        const logoRef = ref(storage, `service-logos/${user.uid}/${Date.now()}`);
        await uploadBytes(logoRef, logoFile);
        logoUrl = await getDownloadURL(logoRef);
      }
      const townId = localStorage.getItem('gather-town-id') || 'loughrea';
      const categoryMeta = CATEGORIES.find(c => c.value === form.category);
      await createService({
        name: form.name.trim(),
        category: form.category,
        emoji: categoryMeta?.emoji || '🏘️',
        description: form.description.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        hours: form.hours.trim() || null,
        address: form.address.trim() || null,
        logoUrl,
        townId,
      }, user.uid);
      toast.success('Added! Thanks for helping build the directory.');
      navigate('/services');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate(-1)} style={s.backBtn}>← Back</button>
      <div style={s.h1}>Add a Service</div>
      <div style={s.sub}>Add a local business, group, or service that should be on the directory. You'll be able to edit it any time afterwards.</div>

      <Field label="Logo (optional)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 14, background: 'var(--sage-pale)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
          }}>
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 24 }}>🏘️</span>
            )}
          </div>
          <label style={s.uploadBtn}>
            {logoFile ? 'Change logo' : 'Upload logo'}
            <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
          </label>
        </div>
      </Field>

      <Field label="Category">
        <select style={s.input} value={form.category} onChange={e => set('category', e.target.value)}>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Name" error={errors.name}>
        <input
          style={{ ...s.input, borderColor: errors.name ? '#D97A6C' : '#C8D9C9' }}
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="e.g. Loughrea Health Hub"
        />
      </Field>

      <Field label="Description (optional)">
        <textarea
          style={{ ...s.input, height: 90, resize: 'vertical' }}
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder="What they do, who it's for..."
        />
      </Field>

      <Field label="Address (optional)">
        <input style={s.input} value={form.address} onChange={e => set('address', e.target.value)} />
      </Field>

      <Field label="Opening hours (optional)">
        <input style={s.input} value={form.hours} onChange={e => set('hours', e.target.value)}
          placeholder="e.g. Mon–Fri 9am–5pm" />
      </Field>

      <Field label="Phone" error={errors.contact}>
        <input style={{ ...s.input, borderColor: errors.contact ? '#D97A6C' : '#C8D9C9' }}
          type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
      </Field>

      <Field label="Email">
        <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
      </Field>

      <Field label="Website">
        <input style={s.input} value={form.website} onChange={e => set('website', e.target.value)}
          placeholder="https://..." />
      </Field>

      <button style={s.submitBtn} disabled={saving} onClick={handleSubmit}>
        {saving ? 'Adding...' : 'Add to directory'}
      </button>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <label style={{ display: 'block', marginBottom: 18 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{label}</div>
    {children}
    {error && <div role="alert" style={{ fontSize: 12, color: '#D97A6C', marginTop: 4 }}>{error}</div>}
  </label>
);

const s = {
  backBtn: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 18 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  sub: { fontSize: 13.5, color: 'var(--muted)', fontWeight: 500, marginBottom: 24, lineHeight: 1.5 },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9',
    fontSize: 14, fontFamily: 'Nunito, sans-serif', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--ink)',
  },
  uploadBtn: {
    padding: '10px 16px', borderRadius: 12, border: '1.5px solid var(--sage-light)',
    background: 'var(--white)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
  submitBtn: {
    width: '100%', padding: '15px 16px', borderRadius: 14, marginTop: 8,
    background: 'var(--sage-deep)', color: 'white', border: 'none',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
};

export default PostServicePage;
